import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Save } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ── Interactive Logo Placement Editor ─────────────────────────────────────────
// Drag the logo freely and resize it via the corner handles on top of a 1:1
// preview. Position & size are stored as percentages of the container so they
// translate 1:1 to the generated image on the backend.
//
// Extracted verbatim from Settings.js so BOTH the Settings page and the new
// BrandDnaView use the SAME component (no duplicate). Behaviour & styling
// unchanged. Props: { overlaySettings, onSave } — onSave receives
// { logo_x_percent, logo_y_percent, logo_size_percent }.
const MIN_SIZE_PCT = 0.05; // 5% of container width
const MAX_SIZE_PCT = 0.50; // 50% of container width

const LogoPlacementEditor = ({ overlaySettings, onSave }) => {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [bg, setBg] = useState('black'); // 'black' | 'white'

  // Position is the top-left corner of the logo, as a fraction of the container.
  // Size is the logo width as a fraction of the container width.
  // Restore from existing percent values; fall back to bottom-right corner @ 7%
  // for legacy users that only have a logo_position string.
  const hasPercent = overlaySettings.logo_x_percent != null
    && overlaySettings.logo_y_percent != null
    && overlaySettings.logo_size_percent != null;
  const [size, setSize] = useState(hasPercent ? overlaySettings.logo_size_percent : 0.07);
  const [pos, setPos] = useState(
    hasPercent
      ? { x: overlaySettings.logo_x_percent, y: overlaySettings.logo_y_percent }
      : { x: 0.92, y: 0.92 } // bottom-right corner
  );

  // Interaction state. Refs hold the live gesture so window listeners always
  // read fresh values without re-binding on every render.
  const [interaction, setInteraction] = useState(null); // 'drag' | 'resize' | null
  const gesture = useRef(null);

  // Logo aspect ratio (height / width) so the preview box matches the real logo.
  const [aspect, setAspect] = useState(1);
  useEffect(() => {
    if (!overlaySettings.logo_url) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth > 0) setAspect(img.naturalHeight / img.naturalWidth);
    };
    img.src = overlaySettings.logo_url;
  }, [overlaySettings.logo_url]);

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

  const onPointerMove = useCallback((e) => {
    const g = gesture.current;
    const container = containerRef.current;
    if (!g || !container) return;
    const rect = container.getBoundingClientRect();
    const dx = (e.clientX - g.startX) / rect.width;
    const dy = (e.clientY - g.startY) / rect.height;

    if (g.mode === 'drag') {
      // Logo width/height as fractions of the container.
      const w = g.size;
      const h = (g.size * aspect * rect.width) / rect.height;
      const nx = clamp(g.posX + dx, 0, 1 - w);
      const ny = clamp(g.posY + dy, 0, 1 - h);
      setPos({ x: nx, y: ny });
    } else if (g.mode === 'resize') {
      // Resize anchored at the logo's top-left corner: grow with the cursor.
      let newSize = clamp(g.size + dx, MIN_SIZE_PCT, MAX_SIZE_PCT);
      // Keep within the container given the fixed top-left anchor.
      newSize = Math.min(newSize, 1 - g.posX);
      const h = (newSize * aspect * rect.width) / rect.height;
      if (g.posY + h > 1) {
        // Limit by height as well.
        const maxByHeight = ((1 - g.posY) * rect.height) / (aspect * rect.width);
        newSize = clamp(maxByHeight, MIN_SIZE_PCT, MAX_SIZE_PCT);
      }
      setSize(newSize);
    }
  }, [aspect]);

  const onPointerUp = useCallback(() => {
    gesture.current = null;
    setInteraction(null);
  }, []);

  useEffect(() => {
    if (!interaction) return;
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
    };
  }, [interaction, onPointerMove, onPointerUp]);

  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    gesture.current = {
      mode: 'drag',
      startX: e.clientX,
      startY: e.clientY,
      posX: pos.x,
      posY: pos.y,
      size,
    };
    setInteraction('drag');
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    gesture.current = {
      mode: 'resize',
      startX: e.clientX,
      startY: e.clientY,
      posX: pos.x,
      posY: pos.y,
      size,
    };
    setInteraction('resize');
  };

  const handleSave = () => {
    onSave({
      logo_x_percent: Number(pos.x.toFixed(4)),
      logo_y_percent: Number(pos.y.toFixed(4)),
      logo_size_percent: Number(size.toFixed(4)),
    });
  };

  // Logo height as a fraction of the container, derived from width + aspect.
  // (Computed inline from the live rect during gestures; for rendering we
  // approximate using a square container so % width === % height visually.)
  const logoStyle = {
    left: `${pos.x * 100}%`,
    top: `${pos.y * 100}%`,
    width: `${size * 100}%`,
  };

  return (
    <div data-testid="logo-placement-editor">
      <label className="text-[#5A6C7D] text-xs block mb-2">{t.settings.visual.placeLogo}</label>

      {/* Background tabs */}
      <div className="flex gap-2 mb-2">
        {[['black', t.settings.visual.bgBlack], ['white', t.settings.visual.bgWhite]].map(([val, label]) => (
          <button
            key={val}
            type="button"
            data-testid={`overlay-bg-${val}`}
            onClick={() => setBg(val)}
            className={`py-1.5 px-3 rounded-lg text-xs transition-all border ${bg === val ? 'bg-[#00D9C8]/20 text-[#00D9C8] border-[#00D9C8]/40' : 'bg-[#0A1628] text-[#5A6C7D] border-transparent hover:text-[#A0AEC0]'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 1:1 preview container */}
      <div
        ref={containerRef}
        data-testid="overlay-preview-container"
        className="relative w-full rounded-lg overflow-hidden select-none border border-[#1A2B42]"
        style={{ aspectRatio: '1 / 1', background: bg === 'black' ? '#000000' : '#FFFFFF' }}
      >
        <div
          data-testid="overlay-draggable-logo"
          onMouseDown={startDrag}
          className="absolute cursor-move"
          style={logoStyle}
        >
          {overlaySettings.logo_url ? (
            <img
              src={overlaySettings.logo_url}
              alt="Logo"
              draggable={false}
              className="w-full h-auto block pointer-events-none"
            />
          ) : (
            <div className="w-full aspect-square bg-[#1A2B42] rounded flex items-center justify-center">
              <span className="text-[#5A6C7D] text-xs">Logo</span>
            </div>
          )}
          {/* Outline + corner resize handles */}
          <div className="absolute inset-0 border border-[#00D9C8]/70 pointer-events-none" />
          <div
            data-testid="overlay-resize-handle"
            onMouseDown={startResize}
            className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 rounded-sm bg-[#00D9C8] border border-white cursor-nwse-resize"
          />
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-[#5A6C7D] text-xs">
          {Math.round(size * 100)}% · {Math.round(pos.x * 100)}% / {Math.round(pos.y * 100)}%
        </span>
        <Button
          type="button"
          size="sm"
          data-testid="overlay-save-placement"
          onClick={handleSave}
          className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00D9C8]/90"
        >
          <Save className="w-4 h-4 mr-1" />
          {t.common.save}
        </Button>
      </div>
    </div>
  );
};

export default LogoPlacementEditor;
